import { IAuthContext } from 'react-oauth2-code-pkce'
import AuthenticationContext, { createAuthContext, isAuthContextFresh } from './AuthenticationContext'

type Listener = (ctx: AuthenticationContext) => void

let latestAuthProviderContext: IAuthContext | undefined
const listeners = new Set<Listener>()

const requireAuthContextError = () =>
  new Error(
    'Authentication context is missing. This may be because your user account is not linked to a user entity. Please try again or contact support for assistance if the problem persists.'
  )

const notifyListeners = (authCtx: AuthenticationContext) => {
  console.debug('[AuthObservable] notifying listeners')
  Array.from(listeners).forEach((listener) => listener(authCtx))
}

export function setGlobalAuthContext(authContext: IAuthContext) {
  console.debug('[AuthObservable] setGlobalAuthContext called', {
    hasToken: !!authContext?.token,
    loginInProgress: authContext?.loginInProgress,
    tokenSuffix: authContext?.token ? authContext.token.slice(-6) : undefined,
    organization: authContext?.idTokenData?.organization_id ?? authContext?.idTokenData?.organizationId,
    user: authContext?.idTokenData?.user_entity_id ?? authContext?.idTokenData?.userEntityId,
  })
  // Only skip if the token is unchanged AND the stored context is already fresh.
  // This allows recovery when a previous call stored a token with incomplete idTokenData.
  const sameToken = latestAuthProviderContext?.token && latestAuthProviderContext.token === authContext.token
  if (sameToken && isAuthContextFresh(latestAuthProviderContext)) return

  latestAuthProviderContext = authContext
  const authCtx = createAuthContext(authContext)
  if (authCtx && isAuthContextFresh(authContext)) {
    console.debug('[AuthObservable] auth context is fresh, notifying')
    notifyListeners(authCtx)
  }
}

export const subscribeToAuthContext = (listener: Listener) => {
  listeners.add(listener)
  console.debug('[AuthObservable] listener added, total', listeners.size)
  const currentAuth = latestAuthProviderContext
  const authCtx = currentAuth ? createAuthContext(currentAuth) : undefined
  if (authCtx && isAuthContextFresh(currentAuth)) {
    console.debug('[AuthObservable] immediate notify on subscribe')
    listener(authCtx)
  }
  return () => listeners.delete(listener)
}

export async function requireAuthContext(timeoutMs: number = 10_000): Promise<AuthenticationContext> {
  const currentAuth = latestAuthProviderContext
  const freshAuthContext = currentAuth ? createAuthContext(currentAuth) : undefined
  if (freshAuthContext && isAuthContextFresh(currentAuth)) return freshAuthContext

  if (currentAuth && !currentAuth.token && !currentAuth.loginInProgress) {
    console.debug('[AuthObservable] requesting login from requireAuthContext (no token present)')
    currentAuth.logIn()
  }

  return new Promise<AuthenticationContext>((resolve, reject) => {
    let settled = false
    let unsubscribe: () => void = () => undefined
    console.debug('[AuthObservable] requireAuthContext waiting for fresh auth context')
    const timeout = setTimeout(() => {
      if (settled) return
      settled = true
      const ctx = latestAuthProviderContext
      console.warn('[AuthObservable] requireAuthContext timed out', {
        hasToken: !!ctx?.token,
        loginInProgress: ctx?.loginInProgress,
        error: ctx?.error,
        hasOrg: !!(ctx?.idTokenData?.organization_id ?? ctx?.idTokenData?.organizationId),
        hasUser: !!(ctx?.idTokenData?.user_entity_id ?? ctx?.idTokenData?.userEntityId),
        tokenExp: ctx?.tokenData?.exp,
        idTokenExp: ctx?.idTokenData?.exp,
        secondsLeft: ctx?.tokenData?.exp ? ctx.tokenData.exp - Math.floor(Date.now() / 1000) : null,
      })
      unsubscribe()
      reject(requireAuthContextError())
    }, timeoutMs)

    unsubscribe = subscribeToAuthContext((authCtx) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      unsubscribe()
      console.debug('[AuthObservable] requireAuthContext resolved from listener')
      resolve(authCtx)
    })
  })
}
